import { ContainerComponent, wrap } from 'AppReact';
import {
  FlexCenterBetweenWrapper, FlexCenterWrapper, Width50, GenericWrapper, Mr2,
  Mr3, Mr1, TooltipWrapper, CursorPointerWrapper, Width60
} from 'wrappers';
import { TagInput, TagText, ClickableDiv, SecondaryTextXs, Tooltip, PropertiesPopup } from 'atoms';
import Controller from './controller';
import internalState from './internalState';
import { TextButton, TagColorSelect } from 'molecules';
import { tagColors } from 'utils';
import {
  ColorPickerSvg, EditPencilSvg, TrashBinSvg, BellSvg, CopySvg, LocationSvg
} from 'components/icons';
import PriorityDropdown from '../priorityDropdown';
import AllMultiPropertySelector from '../allMultiPropertySelector';

const getSelectedProperties = (properties, groups, property_ids, group_ids) => {
  const combined = [...properties, ...groups];
  const result = combined
    .filter(x => property_ids.includes(x.id) || group_ids.includes(x.id))
    .map(x => x.name)
    .join(', ');
  return result;
}

export const definition = {
  name: 'InspectionsTagRow',
  initialProps: ({ tag, maxLength, priorities, properties, groups }) => ({
    tag, maxLength, priorities,
    color: tag?.color ?? '',
    value: tag?.name ?? '',
    priority_id: tag?.priority_id ?? null,
    property_ids: tag?.property_ids ?? [],
    group_ids: tag?.group_ids ?? [],
    auto_add_new_properties: tag?.auto_add_new_properties,
    properties, groups
  }),
  props: internalState,
  controller: Controller,
  elements: [
    {
      wrapper: FlexCenterBetweenWrapper,
      name: 'Wrapper',
      elements: [
        {
          wrapper: GenericWrapper,
          name: 'InputWrapper',
          renderIf: ({ editingField }) => editingField === 'name',
          ref: ({ inputRef }) => inputRef ?? null,
          elements: [
            {
              component: TagInput,
              name: 'TagInput',
              displayState: ({ color }) => ({ color }),
              bindings: ({ value, maxLength, tag, color, priority_id, property_ids, group_ids, auto_add_new_properties }) => {
                return ({ tag, color, maxLength, value, placeholder: 'Tag name', priority_id, property_ids, group_ids, auto_add_new_properties })
              }
            },
          ]
        },
        {
          wrapper: FlexCenterWrapper,
          name: '__transparent__',
          elements: [
            {
              wrapper: Mr3,
              name: 'TagTextWrapper',
              isButton: true,
              bindings: ({ inputRef }) => ({ inputRef }),
              elements: [
                {
                  component: wrap(TagText, CursorPointerWrapper),
                  name: 'TagText',
                  renderIf: ({ editingField }) => editingField !== 'name',
                  displayState: ({ color }) => ({ color }),
                  displayValue: ({ value }) => value
                },
              ]
            },
            {
              component: SecondaryTextXs,
              name: 'PriorityText',
              renderIf: ({ priority_id, priorities, editingField }) => priority_id && priorities.find(p => p.id === priority_id) && editingField !== 'name',
              displayValue: ({ priority_id, priorities }) => priorities.find(p => p.id === priority_id).name.toUpperCase()
            },
          ]
        },
        {
          wrapper: FlexCenterWrapper,
          name: 'ColorsWrapper',
          renderIf: ({ editingField }) => !!editingField,
          elements: [
            {
              component: wrap(TagColorSelect, Mr2),
              name: 'TagColorSelect',
              renderIf: ({ editingField }) => editingField === 'color',
              isListOf: { tagColor: ({ }) => tagColors },
              bindings: ({ color, tagColor, tag }) => ({ tag, color: tagColor, isSelected: color === tagColor }),
              displayState: ({ tagColor }) => ({ color: tagColor })
            },
            {
              component: wrap(PriorityDropdown, Mr2),
              name: 'PrioritySelect',
              renderIf: ({ editingField }) => editingField === 'priority',
              bindings: ({ priorities, priority_id }) => ({ priorities, priority_id })
            },
            {
              component: wrap(AllMultiPropertySelector, Mr2),
              name: 'PropertyContainer',
              renderIf: ({ editingField }) => editingField === 'property',
              bindings: ({ property_ids, group_ids, auto_add_new_properties }) => ({
                properties: window?.properties ?? [],
                groups: window?.groups ?? [],
                selectedPropertyIds: property_ids,
                label: 'Assign to property',
                selectedGroupIds: group_ids,
                silentUpdate: true,
                headerWidth: '300px',
                auto_add_new_properties: auto_add_new_properties,
              })
            },
            {
              component: wrap(TextButton, Mr2),
              name: 'SaveButton',
              bindings: ({ value, color, tag, priority_id, group_ids, property_ids, auto_add_new_properties }) =>
                ({ tag, color, value, title: 'Save', priority_id, group_ids, property_ids, auto_add_new_properties })
            },
            {
              component: TextButton,
              name: 'CancelButton',
              bindings: ({ tag }) => ({ tag, title: 'Cancel' })
            },
          ]
        },
        {
          wrapper: FlexCenterWrapper,
          name: 'Buttons',
          renderIf: ({ editingField }) => !editingField,
          elements: [
            {
              wrapper: wrap(wrap(TooltipWrapper, FlexCenterWrapper), Width50),
              name: 'Priority',
              elements: [
                {
                  wrapper: FlexCenterWrapper,
                  name: 'BellWrapper',
                  elements: [
                    {
                      component: wrap(ClickableDiv, Mr1),
                      name: 'Bell',
                      bindings: ({ tag }) => ({ tag }),
                      displayValue: ({ }) => BellSvg({ width: 18, height: 18, color: window?.settings?.primary_color ?? '#1DBD6B' })
                    },
                    {
                      component: SecondaryTextXs,
                      name: 'Count',
                      renderIf: ({ tag }) => tag.priority_id,
                      displayValue: ({ }) => '1'
                    },
                  ]
                },
                {
                  component: Tooltip,
                  name: 'BellTooltip',
                  displayValue: ({ }) => `Link to a priority \n For work orders generated from inspection items with this tag.`,
                  displayState: ({ }) => ({ direction: 'bottom', width: '225px' })
                }
              ]
            },


            {
              wrapper: wrap(wrap(TooltipWrapper, FlexCenterWrapper), Width60),
              name: 'Location',
              elements: [
                {
                  wrapper: FlexCenterWrapper,
                  name: 'LocationWrapper',
                  elements: [
                    {
                      component: wrap(ClickableDiv, Mr1),
                      name: 'Location',
                      bindings: ({ tag }) => ({ tag }),
                      displayValue: ({ }) => LocationSvg({ width: 20, height: 18, color: window?.settings?.primary_color ?? '#1DBD6B' })
                    },
                    {
                      component: SecondaryTextXs,
                      name: 'Count',
                      displayValue: ({ property_ids, group_ids }) => {
                        let count = (property_ids?.length ?? 0) + (group_ids?.length ?? 0);
                        if (count) return `${count}`;
                        return ''
                      }
                    },
                  ]
                },
                {
                  component: PropertiesPopup,
                  name: 'Popup',
                  displayValue: ({ property_ids, group_ids, properties, groups }) => property_ids?.length > 0 || group_ids?.length > 0 ? getSelectedProperties(properties, groups, property_ids, group_ids) : 'Add properties'
                }
              ]
            },

            {
              wrapper: wrap(wrap(TooltipWrapper, FlexCenterWrapper), Width50),
              name: 'Color',
              elements: [
                {
                  component: ClickableDiv,
                  name: 'Color',
                  bindings: ({ tag }) => ({ tag }),
                  displayValue: ({ }) => ColorPickerSvg({ width: 20, height: 20, color: window?.settings?.primary_color ?? '#1DBD6B' })
                },
                {
                  component: Tooltip,
                  name: 'ColorTooltip',
                  displayValue: ({ }) => `Change color`,
                  displayState: ({ }) => ({ direction: 'bottom', whiteSpace: 'nowrap' })
                }
              ]
            },


            {
              wrapper: wrap(wrap(TooltipWrapper, FlexCenterWrapper), Width50),
              name: 'Name',
              elements: [
                {
                  component: ClickableDiv,
                  name: 'Name',
                  bindings: ({ inputRef, tag }) => ({ inputRef, tag }),
                  displayValue: ({ }) => EditPencilSvg({ width: 18, height: 18, color: window?.settings?.primary_color ?? '#1DBD6B' })
                },
                {
                  component: Tooltip,
                  name: 'NameTooltip',
                  displayValue: ({ }) => `Edit name`,
                  displayState: ({ }) => ({ direction: 'bottom', whiteSpace: 'nowrap' })
                }
              ]
            },

            {
              wrapper: wrap(wrap(TooltipWrapper, FlexCenterWrapper), Width50),
              name: 'Copy',
              elements: [
                {
                  component: ClickableDiv,
                  name: 'Copy',
                  bindings: ({ tag }) => ({ tag }),
                  displayValue: ({ }) => CopySvg({ width: 18, height: 18, color: window?.settings?.primary_color ?? '#1DBD6B' })
                },
                {
                  component: Tooltip,
                  name: 'CopyTooltip',
                  displayValue: ({ }) => `Duplicate`,
                  displayState: ({ }) => ({ direction: 'bottom' })
                }
              ]
            },


            {
              wrapper: wrap(wrap(TooltipWrapper, FlexCenterWrapper), Width50),
              name: 'Delete',
              elements: [
                {
                  component: ClickableDiv,
                  name: 'Delete',
                  bindings: ({ tag }) => ({ tag }),
                  displayValue: ({ }) => TrashBinSvg({ width: 18, height: 18, color: window?.settings?.primary_color ?? '#1DBD6B' })
                },
                {
                  component: Tooltip,
                  name: 'DeleteTooltip',
                  displayValue: ({ }) => `Remove`,
                  displayState: ({ }) => ({ direction: 'bottom' })
                }
              ]
            },
          ]
        }
      ]
    }
  ]
}

export default ContainerComponent(definition);